import { Select as HUISelect } from '@headlessui/react';
import { forwardRef } from 'react';
import { GlobalError } from 'react-hook-form';
import SelectArrowsIcon from '@/assets/select-arrows.svg';
import WarningIcon from '@/assets/warning.svg';
import { cx } from '@/utils/cx';

export type SelectOption<TValue extends string | number = string | number> = {
  label: string;
  value: TValue;
};

export type SelectProps<
  TOptionValue extends string | number = string | number,
> = React.SelectHTMLAttributes<HTMLSelectElement> & {
  error?: GlobalError;
  hidePlaceholderOption?: boolean;
  options: SelectOption<TOptionValue>[];
  placeholder?: string;
};

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      error,
      hidePlaceholderOption,
      options,
      placeholder = 'Select',
      required,
      ...props
    },
    ref,
  ) => {
    const hasNoSelection = props.value === undefined || props.value === '';
    const Icon = error ? WarningIcon : SelectArrowsIcon;

    return (
      <div className="relative">
        <HUISelect
          {...props}
          aria-required={required}
          className={cx(
            'focusable',
            'w-full appearance-none rounded-md border border-black/20 bg-white px-3 py-2 text-sm',
            hasNoSelection && 'text-gray-400',
            error && 'border-warning text-red-900',
            props.className,
          )}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
            }
          }}
          ref={ref}
        >
          {!hidePlaceholderOption && <option value="">{placeholder}</option>}
          {options.map(({ label, value }) => (
            <option
              className="data-focus:bg-blue-100"
              key={value}
              value={value}
            >
              {label}
            </option>
          ))}
        </HUISelect>
        <Icon
          aria-hidden="true"
          className={cx(
            'pointer-events-none absolute top-2/4 right-2 mt-[2px] h-6 w-6 -translate-y-2/4',
            error && 'text-warning h-5 w-5',
          )}
        />
      </div>
    );
  },
);
